import { FC, MutableRefObject, PropsWithChildren, useEffect } from 'react';

import { Box, Paper, Typography } from '@mui/material';
import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { useBreakpoints } from 'hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getSetBusinessPlanChapters } from 'store/useBusinessToolStore/selectors';

import { useBusinessToolPageLogic } from 'pages/BusinessToolPage/hooks/useBusinessToolPageLogic';
import { getBusinessPlanIconDetailsByType } from 'pages/Workspace/helpers/getBusinessPlanIconByType';

import { IBusinessPlanType } from 'interfaces/businessPlan';

import { BottomSlider } from 'components/_basic/BottomSlider';
import { PopperComponent } from 'components/_basic/Popper/Popper';

import styles from './ActionMenuContentView.module.scss';

interface IActionMenuContentViewProps extends PropsWithChildren {
  anchorEl: MutableRefObject<SVGSVGElement | null>;
  isOpened: boolean;
  onClose: VoidFunction;
  docTitle?: string;
  docType?: IBusinessPlanType;
  uuid: string;
}

const ActionMenuDesktopView: FC<IActionMenuContentViewProps> = ({ children, isOpened, onClose, anchorEl, uuid }) => {
  const { initialValues } = useBusinessToolPageLogic({ businessPlanId: String(uuid), disableStoreSave: true });

  const setBusinessPlanChapters = useBusinessToolStore(getSetBusinessPlanChapters);

  useEffect(() => {
    if (isOpened) {
      setBusinessPlanChapters(initialValues);
    }
  }, [isOpened]);

  return (
    <PopperComponent className={styles.menuWrapper} open={isOpened} anchorEl={anchorEl.current} onClose={onClose}>
      <Paper elevation={0} className={styles.popperMenuOptionsContainer}>
        {children}
      </Paper>
    </PopperComponent>
  );
};

const ActionMenuMobileView: FC<IActionMenuContentViewProps> = ({
  children,
  isOpened,
  onClose,
  docType = BUSINESS_PLAN_TYPES.FOR_BANK,
  docTitle = '',
}) => {
  const { cssVariables, icon } = getBusinessPlanIconDetailsByType(docType);

  return (
    <BottomSlider isOpened={isOpened} onClose={onClose}>
      <Box className={styles.mobileTitleWrapper} style={cssVariables}>
        {icon && <Box className={styles.icon}>{icon}</Box>}
        <Typography variant="h6">{docTitle}</Typography>
      </Box>
      {children}
    </BottomSlider>
  );
};

export const ActionMenuContentView: FC<IActionMenuContentViewProps> = (props) => {
  const { isMobile } = useBreakpoints();

  const ContentViewComponent = isMobile ? ActionMenuMobileView : ActionMenuDesktopView;

  return <ContentViewComponent {...props} />;
};
